import { Bold, Heading1, Heading2, Heading3, List, ListOrdered, Redo2, Undo2 } from 'lucide-react';
import { type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/shared/components/ui/Button/Button';

import { MARKDOWN_TOOL_CLASSES } from '../structured.tokens';

import { type MarkdownHeadingLevel, type MarkdownToolbarProps } from './MarkdownToolbar.types';

const HEADING_LEVELS: readonly MarkdownHeadingLevel[] = [1, 2, 3];

const HEADING_ICONS: Record<MarkdownHeadingLevel, ReactNode> = {
  1: <Heading1 aria-hidden className="h-4 w-4" />,
  2: <Heading2 aria-hidden className="h-4 w-4" />,
  3: <Heading3 aria-hidden className="h-4 w-4" />,
};

// Spelled out, so the orphan-key spec can see each one.
const HEADING_LABEL_KEYS: Record<MarkdownHeadingLevel, string> = {
  1: 'markdown.heading1',
  2: 'markdown.heading2',
  3: 'markdown.heading3',
};

/**
 * Exactly the formatting the legal grammar can carry — a button that is not
 * here is a format the member's app would show as raw text.
 */
export function MarkdownToolbar({
  state,
  onToggleHeading,
  onToggleBold,
  onToggleBulletList,
  onToggleOrderedList,
  onUndo,
  onRedo,
}: MarkdownToolbarProps) {
  const { t } = useTranslation('uiKit');

  return (
    <div role="toolbar" aria-label={t('markdown.toolbar')} className="flex flex-wrap gap-2xs">
      {HEADING_LEVELS.map((level) => (
        <Button
          key={level}
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t(HEADING_LABEL_KEYS[level])}
          aria-pressed={state.headingLevel === level}
          className={MARKDOWN_TOOL_CLASSES}
          onClick={() => {
            onToggleHeading(level);
          }}
        >
          {HEADING_ICONS[level]}
        </Button>
      ))}

      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('markdown.bold')}
        aria-pressed={state.isBold}
        className={MARKDOWN_TOOL_CLASSES}
        onClick={onToggleBold}
      >
        <Bold aria-hidden className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('markdown.bulletList')}
        aria-pressed={state.isBulletList}
        className={MARKDOWN_TOOL_CLASSES}
        onClick={onToggleBulletList}
      >
        <List aria-hidden className="h-4 w-4" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('markdown.orderedList')}
        aria-pressed={state.isOrderedList}
        className={MARKDOWN_TOOL_CLASSES}
        onClick={onToggleOrderedList}
      >
        <ListOrdered aria-hidden className="h-4 w-4" />
      </Button>

      {/* Undo points backwards along the reading direction, so it mirrors in RTL. */}
      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('markdown.undo')}
        disabled={!state.canUndo}
        onClick={onUndo}
      >
        <Undo2 aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
      </Button>

      <Button
        type="button"
        variant="ghost"
        size="icon-sm"
        aria-label={t('markdown.redo')}
        disabled={!state.canRedo}
        onClick={onRedo}
      >
        <Redo2 aria-hidden className="h-4 w-4 rtl:-scale-x-100" />
      </Button>
    </div>
  );
}
